import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CircleQuestionMark,
  FlaskConical,
  Info,
  LogOut,
  Settings,
  User,
} from "lucide-react";

import ACL_RELATIONSHIPS from "acl-relationships";
import Authorized from "components/Authorized";
import CopyCCEmail from "components/CopyCCEmail";
import SupportRequestDialog from "components/SupportRequestDialog";
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar";
import { Button } from "components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { useRuvixxFlags } from "providers/RuvixxFlagsProvider";
import AuthService from "services/Auth";
import useAuthStore from "stores/useAuthStore";
import { useUserSettings } from "hooks/useUserSettings";
import { initialsFrom } from "lib/utils";

const USER_SETTINGS_ACLS = [ACL_RELATIONSHIPS.userSettings.read];
const SYSTEM_SETTINGS_ACLS = [
  ACL_RELATIONSHIPS.adminUser.read,
  ACL_RELATIONSHIPS.adminRole.read,
  ACL_RELATIONSHIPS.adminGroup.create,
  ACL_RELATIONSHIPS.disposition.read,
  ACL_RELATIONSHIPS.voicemail.read,
  ACL_RELATIONSHIPS.campaignStatusPreset.readAsTable,
  ACL_RELATIONSHIPS.region.read,
  ACL_RELATIONSHIPS.revenueOpportunityStatus.read,
  ACL_RELATIONSHIPS.constantContactAccounts.read,
  ACL_RELATIONSHIPS.constantContactCampaigns.read,
  ACL_RELATIONSHIPS.customFieldConfig.readTable,
  ACL_RELATIONSHIPS.product.read,
  ACL_RELATIONSHIPS.tag.readTable,
  ACL_RELATIONSHIPS.tagCategory.readTable,
];

export function UserMenu({ isItDialer = false }) {
  const navigate = useNavigate();
  const [supportRequestOpen, setSupportRequestOpen] = useState(false);
  const userAuth = useAuthStore(state => state.userAuth);
  const { data: settings } = useUserSettings();
  const { enable_ui_sandbox } = useRuvixxFlags();

  // The auth store has no avatar, so the picture comes from the settings query;
  // the store's name renders the initials before that query resolves.
  const fullName = settings?.full_name || userAuth?.full_name || "";
  const profilePicture = settings?.user_settings?.account?.profile_picture;

  const logOut = async () => {
    await AuthService.logout();
    navigate("/login");
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          openOnHover
          delay={150}
          closeDelay={150}
          aria-label={
            fullName ? `Account menu for ${fullName}` : "Account menu"
          }
          render={
            <Button variant="ghost" size="icon" className="tw:rounded-full">
              <Avatar>
                <AvatarImage src={profilePicture} alt={fullName} />
                <AvatarFallback>
                  {initialsFrom(fullName) || <User className="tw:size-4" />}
                </AvatarFallback>
              </Avatar>
            </Button>
          }
        />

        <DropdownMenuContent className="tw:w-40" align="start">
          <DropdownMenuGroup>
            {isItDialer && (
              <DropdownMenuItem>
                <CopyCCEmail
                  nopopup={true}
                  className="item"
                  textContent="Copy Email BCC"
                />
              </DropdownMenuItem>
            )}
            <Authorized permissions={USER_SETTINGS_ACLS}>
              <DropdownMenuItem
                render={
                  <Link
                    to="/profile"
                    target={isItDialer ? "_blank" : undefined}
                  />
                }
              >
                <User />
                Profile
              </DropdownMenuItem>
            </Authorized>
            <Authorized permissions={SYSTEM_SETTINGS_ACLS} partialMatch>
              <DropdownMenuItem
                render={
                  <Link
                    to="/settings"
                    target={isItDialer ? "_blank" : undefined}
                  />
                }
              >
                <Settings />
                System Settings
              </DropdownMenuItem>
            </Authorized>
          </DropdownMenuGroup>
          {!isItDialer && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuLabel>Help</DropdownMenuLabel>
                {enable_ui_sandbox && (
                  <DropdownMenuItem render={<Link to="/ui-sandbox" />}>
                    <FlaskConical />
                    UI Sandbox
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  render={
                    <a
                      href="https://pleteo.notion.site/Welcome-to-the-Pleteo-Knowledge-Base-e3cee5febf2b49dea62f0411b3bc2ef7"
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  }
                >
                  <CircleQuestionMark />
                  Wiki / Help Guide
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSupportRequestOpen(true)}>
                  <Info />
                  Submit An Inquiry
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logOut} variant="destructive">
            <LogOut />
            Log Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <SupportRequestDialog
        open={supportRequestOpen}
        onOpenChange={setSupportRequestOpen}
      />
    </>
  );
}
